import { useMemo, useState } from 'react';
import { convertNumberPersianToEnglish } from '@helper/functions';

const SCENARIOS = [
  {
    id: 'recommended',
    title: 'Recommended (text + inputMode="numeric")',
    description: 'همان ورودی استفاده‌شده در فرم ورود پس از اصلاحات اخیر.',
    props: {
      type: 'text',
      inputMode: 'numeric',
      autoComplete: 'tel',
      dir: 'ltr',
      placeholder: 'type="text" + inputMode="numeric"',
    },
    processValue: (raw) => {
      const sanitized = raw.replace(/[^۰-۹0-9]/g, '');
      return convertNumberPersianToEnglish(sanitized);
    },
    focusToEnd: true,
  },
  {
    id: 'text-pattern',
    title: 'text + pattern="[0-9]*"',
    description: 'الگوی قدیمی که روی برخی نسخه‌های iOS مشکل ایجاد می‌کند.',
    props: {
      type: 'text',
      inputMode: 'numeric',
      autoComplete: 'tel',
      dir: 'ltr',
      pattern: '[0-9]*',
      placeholder: 'pattern="[0-9]*"',
    },
    processValue: (raw) => {
      const sanitized = raw.replace(/[^۰-۹0-9]/g, '');
      return convertNumberPersianToEnglish(sanitized);
    },
  },
  {
    id: 'tel',
    title: 'type="tel"',
    description: 'ورودی استاندارد تلفن؛ در بعضی دستگاه‌ها پایدار است.',
    props: {
      type: 'tel',
      inputMode: 'numeric',
      autoComplete: 'tel',
      dir: 'ltr',
      placeholder: 'type="tel"',
    },
    processValue: (raw) => {
      const sanitized = raw.replace(/[^۰-۹0-9]/g, '');
      return convertNumberPersianToEnglish(sanitized);
    },
  },
  {
    id: 'number',
    title: 'type="number"',
    description: 'ورودی عددی بومی مرورگر (اغلب مشکل‌دار در iOS).',
    props: {
      type: 'number',
      dir: 'ltr',
      placeholder: 'type="number"',
    },
    processValue: (raw) => raw,
  },
  {
    id: 'raw-text',
    title: 'text بدون هیچ فیلتر یا تبدیل',
    description: 'برای مشاهده رفتار خام صفحه‌کلید و نگاشت کاراکترها.',
    props: {
      type: 'text',
      dir: 'ltr',
      placeholder: 'text بدون فیلتر',
    },
    processValue: (raw) => raw,
  },
];

export default function TestNumber() {
  const [values, setValues] = useState(() => SCENARIOS.map(() => ''));

  const handleChange = (index, processValue) => (event) => {
    const raw = event.target.value;
    const processed = processValue ? processValue(raw) : raw;

    setValues((prev) => {
      const next = [...prev];
      next[index] = processed;
      return next;
    });
  };

  const clearValues = () => setValues(SCENARIOS.map(() => ''));

  const summary = useMemo(
    () =>
      values.map((value, index) => ({
        id: SCENARIOS[index].id,
        value,
        length: value.length,
      })),
    [values],
  );

  return (
    <div className="min-h-screen w-full bg-slate-50 text-blackFont-900">
      <div className="max-w-4xl mx-auto py-12 px-4 space-y-10">
        <header className="space-y-4">
          <h1 className="text-2xl font-bold">آزمایش ورودی عددی</h1>
          <p className="text-sm text-blackFont-600 leading-6">
            این صفحه برای تست میدانی روی دستگاه‌های iOS قدیمی (۱۵ تا ۱۶) ساخته شده است. برای هر ورودی عدد وارد کنید، یا
            از Paste استفاده کنید و بررسی کنید آیا صفحه‌کلید اجازه تایپ می‌دهد و مقدار به درستی ثبت می‌شود یا خیر.
          </p>
          <button
            onClick={clearValues}
            className="rounded-lg bg-primary/10 text-primary px-4 py-2 text-sm hover:bg-primary/20 transition-all"
          >
            پاک کردن همه مقادیر
          </button>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SCENARIOS.map((scenario, index) => (
            <article
              key={scenario.id}
              className="rounded-xl border border-blackFont-200 bg-white p-5 space-y-4 shadow-sm"
            >
              <div className="space-y-1">
                <h2 className="font-semibold text-base">{scenario.title}</h2>
                <p className="text-xs text-blackFont-500 leading-5">{scenario.description}</p>
              </div>

              <label className="flex flex-col gap-2 text-xs text-blackFont-500">
                مقدار وارد شده
                <input
                  {...scenario.props}
                  value={values[index]}
                  onChange={handleChange(index, scenario.processValue)}
                  onFocus={(event) => {
                    if (scenario.focusToEnd) {
                      const { value } = event.target;
                      event.target.setSelectionRange(value.length, value.length);
                    }
                  }}
                  className="w-full rounded-lg border border-blackFont-200 px-3 py-2 text-base text-blackFont-900 focus:border-blackFont-900 focus:outline-none focus:ring-0"
                />
              </label>

              <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-blackFont-600 leading-5">
                <strong className="font-semibold">state:</strong> <span dir="ltr">{values[index] || '""'}</span>
                <br />
                <strong className="font-semibold">length:</strong> {values[index].length}
              </div>
            </article>
          ))}
        </section>

        <section className="rounded-xl border border-dashed border-blackFont-300 bg-white p-5 space-y-3">
          <h2 className="font-semibold text-base">خلاصهٔ مقدارها</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-blackFont-600">
            {summary.map((item) => (
              <div key={item.id} className="rounded-lg bg-slate-100 px-3 py-2">
                <span className="font-semibold">{item.id}</span>
                <div className="flex justify-between items-center mt-1">
                  <span dir="ltr" className="truncate max-w-[70%]">
                    {item.value || '""'}
                  </span>
                  <span>{item.length} رقم</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
